/**
 * Region-based display/charge currency.
 *
 * CentroBill settles everything in EUR regardless of the currency displayed
 * or sent in API requests (confirmed by their support, 2026-08-18), so the
 * choice of customer-facing currency is free. We show USD to US/rest-of-world
 * visitors and EUR to Europeans, with IDENTICAL numerals (€2.99 → $2.99).
 *
 * Pure, isomorphic module: imported by the root server layout, the billing
 * checkout route, and client components alike. Keep it free of server-only
 * or client-only imports.
 */

export type DisplayCurrency = 'USD' | 'EUR';

/**
 * Countries whose visitors see EUR: EU-27, EEA/adjacent (IS LI NO CH GB) and
 * the euro microstates. Everyone else — including requests with no
 * x-vercel-ip-country header (local dev) — sees USD.
 */
export const EUR_COUNTRIES: ReadonlySet<string> = new Set([
  // EU-27
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  // EEA / adjacent
  'IS',
  'LI',
  'NO',
  'CH',
  'GB',
  // Euro microstates & euro-using territories
  'AD',
  'MC',
  'SM',
  'VA',
  'XK',
  'ME',
]);

export function resolveCurrencyFromCountry(
  country: string | null | undefined,
): DisplayCurrency {
  return country && EUR_COUNTRIES.has(country.toUpperCase()) ? 'EUR' : 'USD';
}

/**
 * Visitor country from request headers. edemai.org sits behind Cloudflare,
 * so Vercel's x-vercel-ip-country reflects the CLOUDFLARE EDGE that proxied
 * the request (observed 2026-08-19: a Kazakhstan visitor rendered EUR because
 * CF routed through Warsaw). cf-ipcountry carries the real client country, so
 * it wins; Vercel's header covers direct-to-Vercel setups (previews, local
 * spoofing). CF sends 'XX' (unknown) and 'T1' (Tor) — treated as unknown.
 */
export function resolveCurrencyFromRequestHeaders(headers: {
  get(name: string): string | null;
}): DisplayCurrency {
  const cf = headers.get('cf-ipcountry');
  const country =
    cf && cf.length === 2 && cf !== 'XX'
      ? cf
      : headers.get('x-vercel-ip-country');
  return resolveCurrencyFromCountry(country);
}

export function currencySymbol(currency: DisplayCurrency): '$' | '€' {
  return currency === 'EUR' ? '€' : '$';
}

/**
 * Symbol-prefix formatting matching the existing UI (`€2.99` → `$2.99`).
 * Deliberately not Intl.NumberFormat: locale formatting would emit "2,99 €"
 * and break the design's symbol-first price treatment.
 */
export function formatPrice(
  value: number | string,
  currency: DisplayCurrency,
): string {
  const num = typeof value === 'number' ? value.toFixed(2) : value;
  return `${currencySymbol(currency)}${num}`;
}
